import { observer } from "mobx-react-lite";
import SmartApp from "./SmartApp";
import { useNavigate } from "react-router-dom";
import useUrlQueryParams from "e.sic-platform.webclient/src/services/url/UrlQueryService";
import { useEffect, useState } from "react";
import { Grid, Stack, Typography } from "@mui/material";
import LoadingSpinner from "e.sic-platform.webclient/src/components/loading-spinner/LoadingSpinner";
import SmartAppStep from "./SmartAppStep";
import DropFiles from "e.sic-platform.webclient/src/components/upload-drop-files/DropFiles";
import { uploadFilesService } from "e.sic-platform.webclient/src/services/upload/UploadFilesService";
import UploadTable from "e.sic-platform.webclient/src/components/upload-drop-files/UploadTable";
import PurchaseConfirmation from "./PurchaseConfirmation";
import UploadStatus from "e.sic-platform.webclient/src/components/upload-status/UploadStatus";
import CopyUrlButton from "../buttons/copy-url-button/CopyUrlButton";
import { AppStorage } from "e.sic-platform.webclient/src/services/app/AppStorage";
import RunAppButton from "../buttons/run-app-button/RunAppButton";
import { Icon } from "e.sic-platform.webclient/src/icons/Icon";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";
import { PubSubEvents } from "e.sic-platform.webclient/src/services/PubSubEvents";
import { endpoints } from "e.sic-platform.webclient/src/services/api/ApiEndpoints";
import { IServiceStorageEnv } from "e.sic-platform.webclient/src/services/app/ServiceStorage";

export interface ISmartAppBaseProps {
    appStorage: AppStorage,
    icon: Icon | undefined,
    actionBar?: any;
    children: any
}

export default observer(function SmartAppBase(props: ISmartAppBaseProps) {
    return (
        <SmartApp
            appStorage={props.appStorage}
            actionBar={props.actionBar}
        >
            <AppPage appStorage={props.appStorage} icon={props.icon}>
                {props.children}
            </AppPage>
        </SmartApp>
    )
})

export const AppPage = observer(function AppPage(props: ISmartAppBaseProps) {
    const {appStorage, icon} = {...props};
    const navigate = useNavigate();
    const { guid }: any = useUrlQueryParams();
    const [confirmationPending, setConfirmationPending] = useState(false);

    useEffect(() => {
        if (appStorage.navigate !== "") {
            navigate(appStorage.navigate)
            appStorage.resetNavigate()
        }
    }, [appStorage.navigate, appStorage.resetNavigate])

    useEffect(() => {
    async function loadIds() {
      try {
            if (guid !== undefined) {
            appStorage.setFileId(await appStorage.getFileId(guid));
            appStorage.setFileGuid(guid)
            appStorage.load();
        }
        if (appStorage.fileId !== undefined) {
            navigate(appStorage.fileRoute);
        }
      } catch (err) {
        appStorage.setError("general.apps.error")
      }
    }
    loadIds()
    }, [guid])

    useEffect(() => {
        uploadFilesService.clear();
    }, [])

    useEffect(() => {
        const subscribeToken = PubSub.subscribe(
            PubSubEvents.VideoConversionFinished,
            (_event: string, notification: Notification) => {
                console.log(notification);
                appStorage.onConversionFinished(notification.data.fileId);
            }
      );
    }, [appStorage.onConversionFinished])

    if (appStorage.fileId === undefined) appStorage.setStep(1);
    if (appStorage.fileId && appStorage.step<2) appStorage.setStep(2);

    if (appStorage.errorMessage) return <Grid item xs={12}><Typography color="red">{translationService.translate(appStorage.errorMessage)}</Typography></Grid>
    if (appStorage.isLoading) return <Grid item xs={12}><LoadingSpinner /></Grid>

    return (
        <>
            <SmartAppStep
                titleKey="videoAnalysis.upload.title"
                descriptionKey="videoAnalysis.upload.description"
                visible={appStorage.fileId === undefined}
                step={1}
                appStorage={appStorage}
            >
                <Grid item xs={12} sm={12}>
                    <DropFiles singleFile onDroppedFiles={uploadFilesService.addFilesForUpload} />
                </Grid>
                <Grid item xs={12} sm={12} className="centered">
                    {appStorage.canRun &&
                        <UploadTable files={uploadFilesService.filesForUpload} onRemoveFile={uploadFilesService.removeFile} />
                    }
                </Grid>
                <Grid item xs={12} sm={12} className="centered">
                    <Stack direction="row" alignItems="center">
                        <RunAppButton disabled={!appStorage.canRun} onClick={() => setConfirmationPending(!confirmationPending)} icon={icon}/>
                        <PurchaseConfirmation
                            apps={appStorage.activityType}
                            visible={confirmationPending}
                            confirm={appStorage.upload}
                            toggle={() => setConfirmationPending(!confirmationPending)}
                        />
                    </Stack>
                </Grid>
                <Grid item xs={12} sm={12}>
                    {uploadFilesService.uploadProgressModalIsVisbile && <UploadStatus />}
                </Grid>
            </SmartAppStep>
            {props.children}
            <Grid item xs={12} sm={12}>
                {appStorage.fileId !== undefined && <CopyUrlButton id={appStorage.fileId} />}
            </Grid>
        </>
    )
})